import type { DashboardResponse, MetricValue } from "@broadview/api-client";
import { formatMetricDisplayValue } from "./metric-display";

export type DashboardWidgetState =
  "ready" | "stale" | "insufficient" | "unreconciled" | "unavailable" | "contract-error";

export interface DashboardWidgetViewModel {
  widgetId: string;
  definitionId?: string;
  label: string;
  displayValue?: string;
  state: DashboardWidgetState;
  stateDetail: string;
  asOf?: string;
  sourceSummary: string;
  metric?: MetricValue;
}

export interface DashboardViewModel {
  snapshotId: string;
  tenantId: string;
  view: string;
  generatedAt: string;
  dataAsOf: string;
  widgets: DashboardWidgetViewModel[];
}

function contractError(
  widget: DashboardResponse["widgets"][number],
  detail: string,
  hasEvidenceTimestamp: boolean,
): DashboardWidgetViewModel {
  return {
    widgetId: widget.widgetId,
    label: widget.widgetId,
    state: "contract-error",
    stateDetail: detail,
    asOf: hasEvidenceTimestamp ? widget.asOf : undefined,
    sourceSummary: widget.sourceSummary,
  };
}

export function widgetState(metric: MetricValue): Exclude<DashboardWidgetState, "contract-error"> {
  if (metric.dataState === "UNAVAILABLE") return "unavailable";
  if (metric.quality === "INSUFFICIENT") return "insufficient";
  if (metric.reconciliation === "EXCEPTION" || metric.reconciliation === "UNKNOWN") {
    return "unreconciled";
  }
  if (metric.freshness === "STALE" || metric.freshness === "UNKNOWN") return "stale";
  return "ready";
}

export function buildDashboardViewModel(
  dashboard: DashboardResponse,
  expectedTenantId: string,
  parseMetric: (payload: unknown) => MetricValue | undefined,
): DashboardViewModel {
  const snapshotId = `${dashboard.tenantId}:${dashboard.view}:${dashboard.generatedAt}:${dashboard.dataAsOf}`;
  const tenantMatches = dashboard.tenantId === expectedTenantId;

  return {
    snapshotId,
    tenantId: dashboard.tenantId,
    view: dashboard.view,
    generatedAt: dashboard.generatedAt,
    dataAsOf: dashboard.dataAsOf,
    widgets: dashboard.widgets.map((widget) => {
      const hasEvidenceTimestamp = !dashboard.warnings.some(
        ({ code, widgetIds }) =>
          code === "METRIC_TIMESTAMP_UNAVAILABLE" && widgetIds?.includes(widget.widgetId),
      );
      if (!tenantMatches) {
        return contractError(
          widget,
          "Dashboard tenant does not match the selected Division.",
          hasEvidenceTimestamp,
        );
      }
      const metric = parseMetric(widget.payload);
      if (!metric)
        return contractError(
          widget,
          "Widget payload does not match the governed metric contract.",
          hasEvidenceTimestamp,
        );
      if (metric.tenantId !== expectedTenantId) {
        return contractError(
          widget,
          "Metric tenant does not match the selected Division.",
          hasEvidenceTimestamp,
        );
      }
      const state = widgetState(metric);
      return {
        widgetId: widget.widgetId,
        definitionId: metric.definitionId,
        label: metric.label,
        displayValue: state === "unavailable" ? undefined : formatMetricDisplayValue(metric),
        state,
        stateDetail:
          state === "ready"
            ? "Governed result is current and reconciled."
            : state === "stale"
              ? "The governed result is stale or has unknown freshness."
              : state === "insufficient"
                ? "The governed result has insufficient supporting data."
                : state === "unreconciled"
                  ? "The governed result has not passed reconciliation."
                  : "No governed result is available; no prototype value was substituted.",
        asOf: hasEvidenceTimestamp ? metric.asOf : undefined,
        sourceSummary: metric.sourceSummary,
        metric,
      };
    }),
  };
}
